import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  styled,
  Collapse,
  IconButton,
  Chip,
  Avatar,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import EngineeringIcon from "@mui/icons-material/Engineering";
import BusinessIcon from "@mui/icons-material/Business";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export interface UserNode {
  userId: string;
  superiorId: string | null;
  superiorFirstName: string | null;
  depth: number;
  campaignId: string;
  role: "brand" | "agency" | "supervisor" | "executor";
  userFirstName: string;
  phoneNumber: number | null;
  children: UserNode[];
}

interface CampaignViewProps {
  data: UserNode[];
}

const typeColors = {
  brand: { bg: "#E3F2FD", color: "#1976D2", icon: "#1976D2" },
  agency: { bg: "#F3E5F5", color: "#7B1FA2", icon: "#7B1FA2" },
  supervisor: { bg: "#E8F5E8", color: "#388E3C", icon: "#388E3C" },
  executor: { bg: "#FFF3E0", color: "#F57C00", icon: "#F57C00" },
};

const getIcon = (type: string, sx = {}) => {
  const color = typeColors[type as keyof typeof typeColors]?.icon || "#666";
  const iconProps = { fontSize: "small" as const, sx: { color, ...sx } };

  switch (type) {
    case "brand":
      return <PersonIcon {...iconProps} />;
    case "agency":
      return <BusinessIcon {...iconProps} />;
    case "supervisor":
      return <SupervisorAccountIcon {...iconProps} />;
    case "executor":
      return <EngineeringIcon {...iconProps} />;
    default:
      return <PersonIcon {...iconProps} />;
  }
};

const StyledCard = styled(Card)({
  borderRadius: 16,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
  width: "100%",
  maxWidth: "100%",
  overflowX: "hidden",
});

const TreeNode = styled(Box)({
  position: "relative",
  width: "100%",
});

const NodeItem = styled(Paper)<{ isMobile: boolean }>(({ isMobile }) => ({
  margin: isMobile ? "2px 0" : "4px 0",
  padding: isMobile ? "4px 6px" : "8px",
  backgroundColor: "#ffffff",
  transition: "all 0.2s ease-in-out",
  overflowX: "hidden",
  width: "100%",
  "&:hover": {
    backgroundColor: "#f1edd3",
  },
}));

const NodeContentWrapper = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  width: "100%",
});

const NodeContent = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  width: "100%",
});

const NodeAvatar = styled(Avatar)<{ isMobile: boolean }>(({ isMobile }) => ({
  marginRight: isMobile ? "8px" : "12px",
  flexShrink: 0,
}));

const ChildrenContainer = styled(Box)<{ isMobile: boolean }>(
  ({ isMobile }) => ({
    marginLeft: isMobile ? "16px" : "32px",
    paddingLeft: isMobile ? "6px" : "16px",
    borderLeft: "2px solid rgba(0, 0, 0, 0.2)",
    width: "100%",
    overflowX: "hidden",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: -1,
      top: 0,
      bottom: 0,
      width: 2,
      background:
        "linear-gradient(to bottom, transparent, rgba(102, 126, 234, 0.3), transparent)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      left: -5,
      bottom: 0,
      width: 12,
      height: 2,
      background:
        "linear-gradient(to bottom, transparent, rgba(3, 3, 3, 0.3), transparent)",
      borderRadius: 1,
      zIndex: 2,
      display: "block",
    },
  })
);

const RenderUserNode: React.FC<{
  node: UserNode;
  level?: number;
  isMobile: boolean;
}> = ({ node, level = 0, isMobile }) => {
  const [expanded, setExpanded] = React.useState(true);
  const hasChildren = node?.children && node?.children.length > 0;
  const typeConfig = typeColors[node?.role] || typeColors.brand;

  return (
    <TreeNode>
      <NodeItem elevation={0} isMobile={isMobile}>
        <NodeContentWrapper>
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
            {hasChildren && (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{
                  color: "text.secondary",
                }}
              >
                {expanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </IconButton>
            )}
          </Box>

          <NodeContent>
            <NodeAvatar
              isMobile={isMobile}
              sx={{
                backgroundColor: typeConfig.bg,
                width: isMobile ? 24 : 33,
                height: isMobile ? 24 : 33,
              }}
            >
              {getIcon(node.role)}
            </NodeAvatar>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: isMobile ? 0.5 : 1,
                  flexWrap: "wrap",
                  wordBreak: "break-word",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: level === 0 ? 600 : 500,
                    fontSize: isMobile
                      ? "0.75rem"
                      : level === 0
                      ? "1rem"
                      : "0.875rem",
                    color: "text.primary",
                    mb: 0.2,
                    wordBreak: "break-word",
                  }}
                >
                  {node.userFirstName}
                </Typography>
                <Chip
                  label={node.role}
                  size="small"
                  sx={{
                    backgroundColor: typeConfig.bg,
                    color: typeConfig.color,
                    fontSize: isMobile ? "0.6rem" : "0.75rem",
                    fontWeight: 500,
                    textTransform: "capitalize",
                    height: isMobile ? 18 : 22,
                  }}
                />
              </Box>

              {node?.phoneNumber && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    ml: 1,
                    fontSize: isMobile ? "0.6rem" : "0.75rem",
                    color: "text.secondary",
                    wordBreak: "break-word",
                  }}
                >
                  ({node.phoneNumber})
                </Typography>
              )}
            </Box>
          </NodeContent>
        </NodeContentWrapper>
      </NodeItem>

      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <ChildrenContainer isMobile={isMobile}>
            {node.children.map((child) => (
              <RenderUserNode
                key={child.userId}
                node={child}
                level={level + 1}
                isMobile={isMobile}
              />
            ))}
          </ChildrenContainer>
        </Collapse>
      )}
    </TreeNode>
  );
};

const CampaignView: React.FC<CampaignViewProps> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <StyledCard>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: isMobile ? 1 : 2,
          mb: isMobile ? 1 : undefined,
          ml: isMobile ? 1 : 3,
          mr: isMobile ? 2 : 3,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{
            fontSize: isMobile ? "1rem" : "1.3rem",
          }}
        >
          Campaign Hierarchy
        </Typography>
        {data[0]?.superiorFirstName && (
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: isMobile ? "0.75rem" : "1rem",
              color: "text.secondary",
            }}
          >
            <Box
              component="span"
              sx={{ color: "text.primary", fontWeight: 500 }}
            >
              Assigned by:
            </Box>{" "}
            {data[0].superiorFirstName}
          </Typography>
        )}
      </Box>
      <CardContent
        sx={{
          flex: 1,
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          p: isMobile ? 0.5 : 2,
          width: "100%",
        }}
      >
        <Box sx={{ flex: 1, overflowX: "hidden", width: "100%" }}>
          {data.map((node) => (
            <RenderUserNode key={node.userId} node={node} isMobile={isMobile} />
          ))}
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default CampaignView;
